import { Edit, Plus, Shield, Trash2 } from 'lucide-react';
import { useMemo, useState, type FC, type JSX } from 'react';
import { toast } from '../../../lib/utils/toast';
import { getErrorMessage } from '../../../lib/utils/helpers';
import type { AvailablePermissions, Role } from '../../../services/api/roles/roleApi';
import {
  useCreateRole,
  useDeleteRole,
  useGetAvailablePermissions,
  useGetRoles,
  useUpdateRole
} from '../../../services/query/roles/role';
import Button from '../../shared/Button';
import ConfirmModal from '../../shared/ConfirmModal';
import FormField from '../../shared/FormField';
import Input from '../../shared/Input';
import Modal from '../../shared/Modal';
import Pagination from '../../shared/Pagination';
import Textarea from '../../shared/Textarea';

type RoleFormState = {
  name: string;
  description: string;
  is_active: boolean;
  permissions: string[];
};

const RolesTab: FC = (): JSX.Element => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);

  const { data: permissions, isLoading: permissionsLoading } = useGetAvailablePermissions();
  const { data: rolesResponse, isLoading: rolesLoading } = useGetRoles({ page, limit: 10 });

  const { mutate: createRole, isPending: creating } = useCreateRole();
  const { mutate: updateRole, isPending: updating } = useUpdateRole();
  const { mutate: deleteRole, isPending: deleting } = useDeleteRole();

  const roles = useMemo(() => rolesResponse?.data ?? [], [rolesResponse?.data]);
  const pagination = rolesResponse?.pagination;

  const filteredRoles = useMemo(() => {
    const s = searchTerm.trim().toLowerCase();
    if (!s) return roles;
    return roles.filter((r) => {
      return (
        r.name.toLowerCase().includes(s) ||
        r.description?.toLowerCase().includes(s) ||
        r.permissions?.some((p) => p.toLowerCase().includes(s))
      );
    });
  }, [roles, searchTerm]);

  const openCreate = () => {
    setEditingRole(null);
    setShowForm(true);
  };

  const openEdit = (role: Role) => {
    setEditingRole(role);
    setShowForm(true);
  };

  const openDelete = (role: Role) => {
    setRoleToDelete(role);
    setConfirmDeleteOpen(true);
  };

  const onCloseForm = () => {
    setShowForm(false);
    setEditingRole(null);
  };

  const onCloseDelete = () => {
    setConfirmDeleteOpen(false);
    setRoleToDelete(null);
  };

  const handleDelete = () => {
    if (!roleToDelete) return;
    deleteRole(roleToDelete.id, {
      onSuccess: () => {
        toast.success('Role deleted');
        onCloseDelete();
      },
      onError: (error: unknown) => {
        toast.error(getErrorMessage(error, 'Failed to delete role'));
      }
    });
  };

  const loading = rolesLoading || permissionsLoading;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Total Roles</h3>
            <Shield className="w-6 h-6 text-blue-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{pagination?.total_items ?? roles.length}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Role Management</h2>
          <Button leftIcon={<Plus className="w-5 h-5" />} onClick={openCreate} type="button">
            New Role
          </Button>
        </div>

        <div className="mb-6">
          <FormField label="Search">
            <Input
              type="text"
              placeholder="Search by name, description, or permission..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={loading}
            />
          </FormField>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Permissions</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRoles.map((role) => (
                <tr key={role.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{role.name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-700">{role.description || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        role.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {role.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-700">{role.permissions?.length ?? 0}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => openEdit(role)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Edit"
                        type="button"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openDelete(role)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                        type="button"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {!loading && filteredRoles.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-gray-500">
                    No roles found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {pagination ? (
          <Pagination
            pagination={pagination}
            currentPage={page}
            onPageChange={setPage}
            itemName="roles"
          />
        ) : null}
      </div>

      <RoleFormModal
        open={showForm}
        onClose={onCloseForm}
        role={editingRole}
        availablePermissions={permissions?.permissions ?? []}
        loading={creating || updating}
        onSubmit={(payload) => {
          if (editingRole) {
            updateRole(
              { id: editingRole.id, payload },
              {
                onSuccess: () => {
                  toast.success('Role updated');
                  onCloseForm();
                },
                onError: (error: unknown) => {
                  toast.error(getErrorMessage(error, 'Failed to update role'));
                }
              }
            );
          } else {
            createRole(payload, {
              onSuccess: () => {
                toast.success('Role created');
                onCloseForm();
              },
              onError: (error: unknown) => {
                toast.error(getErrorMessage(error, 'Failed to create role'));
              }
            });
          }
        }}
      />

      <ConfirmModal
        open={confirmDeleteOpen}
        onClose={onCloseDelete}
        onConfirm={handleDelete}
        title="Delete role"
        description={`Delete role "${roleToDelete?.name ?? ''}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="danger"
        loading={deleting}
      />
    </div>
  );
};

const RoleFormModal: FC<{
  open: boolean;
  onClose: () => void;
  role: Role | null;
  availablePermissions: AvailablePermissions['permissions'] ;
  loading: boolean;
  onSubmit: (payload: RoleFormState) => void;
}> = ({ open, onClose, role, availablePermissions, loading, onSubmit }): JSX.Element => {
  const [form, setForm] = useState<RoleFormState>(() => {
    return {
      name: role?.name ?? '',
      description: role?.description ?? '',
      is_active: role?.is_active ?? true,
      permissions: role?.permissions ?? []
    };
  });

  // Re-init when opening for a different role
  useMemo(() => {
    if (!open) return null;
    setForm({
      name: role?.name ?? '',
      description: role?.description ?? '',
      is_active: role?.is_active ?? true,
      permissions: role?.permissions ?? []
    });
    return null;
  }, [open, role]);

  const togglePermission = (permission: string) => {
    setForm((prev) => {
      const exists = prev.permissions.includes(permission);
      return {
        ...prev,
        permissions: exists
          ? prev.permissions.filter((p) => p !== permission)
          : [...prev.permissions, permission]
      };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.name.trim()) {
      toast.error('Role name is required');
      return;
    }

    onSubmit({
      name: form.name.trim(),
      description: form.description,
      is_active: form.is_active,
      permissions: form.permissions
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={role ? 'Edit Role' : 'New Role'}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button" disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit as unknown as () => void} type="button" loading={loading}>
            {role ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4 h-96 overflow-y-auto pr-2">
        <FormField label="Name" required>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            disabled={loading}
            required
          />
        </FormField>

        <FormField label="Description">
          <Textarea
            rows={3}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            disabled={loading}
          />
        </FormField>

        <div className="flex items-center justify-between rounded-lg border border-gray-200 p-4">
          <div>
            <div className="font-medium text-gray-900">Active</div>
            <div className="text-sm text-gray-600">Disable to prevent assignment</div>
          </div>
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
            disabled={loading}
          />
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-700">Permissions</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[40vh] overflow-y-auto border border-gray-200 rounded-lg p-3">
            {availablePermissions.length ? (
              availablePermissions.map((perm) => (
                <label
                  key={perm}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={form.permissions.includes(perm)}
                    onChange={() => togglePermission(perm)}
                    className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    disabled={loading}
                  />
                  <span className="text-sm text-gray-900">{perm}</span>
                </label>
              ))
            ) : (
              <div className="text-sm text-gray-500">No permissions available.</div>
            )}
          </div>
          <div className="text-xs text-gray-500">{form.permissions.length} selected</div>
        </div>
      </form>
    </Modal>
  );
};

export default RolesTab;
